import { create } from 'zustand';
import api from '@/features/api/axios';
import { logger } from '@/features/help/utils/logger';
import { FormStructure, FormField, FieldValue } from '../types/forms';
import { toast } from 'react-hot-toast';
import type { LegalContext } from '../types/legal-context.types';

import { useSignerStore } from './signer.store';
import { AxiosError } from 'axios';

interface ErrorResponse {
  message?: string;
}

interface ApplicationStore {
  structure: FormStructure | null;
  loading: boolean;
  error: string | null;
  recordId: string | null;
  isEditable: boolean;
  hasMappings: boolean;
  primaryCrmObject: string | null;
  crmId: string | null;
  recordOwnerId: string | null;

  viewableFields: string[];
  editableFields: string[];
  files: Record<string, File>;
  recordActionStatus: string | null;
  contactConsent: boolean;
  termsConsent: boolean;
  legalContext: LegalContext | null;
  isBrokerTrialEnded: boolean;
  userShortId: string | null;
  userId: string | null;
  pdfOnly: boolean;
  allowAdditionalSignerDocUpload: boolean;
  currentSignerCompleted: boolean;
  progress?: {
    lastCompletedPageIndex: number;
    lastCompletedPageId: string | null;
    lastSeenTotalPages: number;
  };

  setContactConsent: (consent: boolean) => void;
  setTermsConsent: (consent: boolean) => void;
  setPrimaryCrmObject: (primaryCrmObject: string) => void;
  setCrmId: (crmId: string) => void;
  setRecordOwnerId: (recordOwnerId: string | null) => void;

  // Structure Management
  initializeFromApiPayload: (payload: any) => void;
  setStructure: (structure: FormStructure) => void;
  updateStructure: (newStructure: FormStructure) => void;

  // Field Management
  handleFieldUpdate: (fieldId: string, updates: Partial<FormField>) => void;
  handleFieldValueChange: (fieldId: string, value: FieldValue) => void;
  updateFile: (fieldId: string, file: File) => void;

  // Form State
  setIsEditable: (isEditable: boolean) => void;
  submitForm: (
    captchaToken: string,
    mappingShortId: string,
    progressHeaders?: { pageCompletedIndex?: number; pageCompletedId?: string; totalVisiblePages?: number }
  ) => Promise<{ success: boolean }>;

  // State Management
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setBrokerTrialEnded: (ended: boolean) => void;

  // Getters
  getUserId: () => string;
}

// Track the last submission time to prevent duplicate submissions
let lastSubmissionTime = 0;
const SUBMISSION_DEBOUNCE_MS = 2000; // 2 seconds

// Helper functions for invalid fields removed - now handled by validationStore

// NEW HELPER FUNCTION FOR CLEARING FILES
const _clearFilesForFieldId = (
  currentFiles: Record<string, File>,
  fieldIdToRemove: string
): Record<string, File> => {
  const newFilesMap = { ...currentFiles };
  for (const key in newFilesMap) {
    if (key === fieldIdToRemove || key.startsWith(`${fieldIdToRemove}_`)) {
      delete newFilesMap[key];
    }
  }

  return newFilesMap;
};
// END OF HELPER FUNCTION FOR CLEARING FILES

export const useApplicationStore = create<ApplicationStore>((set, get) => ({
  structure: null,
  loading: false,
  error: null,
  recordId: null,
  isEditable: true,
  hasMappings: false,
  primaryCrmObject: null,
  crmId: null,
  recordOwnerId: null,

  viewableFields: [],
  editableFields: [],
  files: {},
  recordActionStatus: null,
  contactConsent: false,
  termsConsent: false,
  legalContext: null,
  isBrokerTrialEnded: false,
  userShortId: null,
  userId: null,
  pdfOnly: false,
  allowAdditionalSignerDocUpload: false,
  currentSignerCompleted: false,
  progress: undefined,

  // Computed Values

  // Purpose: Initialize application state from API payload (used after redirect logic)
  initializeFromApiPayload: (payload) => {

    const receivedStructure = payload.structure;
    const recordStatus = payload.recordStatus || null;
    const currentSignerIndex = payload.currentSignerIndex || 1;
    const userIdFromResponse = payload.userId || null;
    const progress = payload.progress || undefined;
    const hasCompletedAnyPageFromProgress =
      typeof progress?.lastCompletedPageIndex === 'number' && progress.lastCompletedPageIndex >= 0;

    // Preserve contact consent when already set in-memory, and recover it on hydrated sessions
    // using persisted progress/timestamp evidence from backend payload.
    const existingContactConsent = get().contactConsent === true;
    const hasPageOneCompletionTimestamp = Boolean(
      receivedStructure?.pages?.some((page: any) =>
        page?.sections?.some((section: any) =>
          section?.fields?.some(
            (field: any) =>
              field?.id === 'page1CompleteTimestamp' &&
              typeof field?.value === 'string' &&
              field.value.trim() !== ''
          )
        )
      )
    );
    const hydratedContactConsent =
      existingContactConsent || hasCompletedAnyPageFromProgress || hasPageOneCompletionTimestamp;

    logger.info('Application Store', 'initializeFromApiPayload: contact consent hydration computed', {
      existingContactConsent,
      hasCompletedAnyPageFromProgress,
      hasPageOneCompletionTimestamp,
      hydratedContactConsent,
    });

    // Initialize signer store first
    const signerStore = useSignerStore.getState();
    signerStore.initializeFromApiPayload({
      recordStatus,
      currentSignerIndex,
      signerShortId: payload.signerShortId,
    });

    // Calculate permissions for the current signer using actual signer index
    const { viewableFields, editableFields } = signerStore.updateSignerPermissions(
      currentSignerIndex,
      receivedStructure,
      {
        allowAdditionalSignerDocUpload: Boolean(payload.allowAdditionalSignerDocUpload),
      }
    );

    // Update application store state
    set({
      structure: receivedStructure,
      viewableFields,
      editableFields,
      recordActionStatus: recordStatus,
      error: null,
      hasMappings: true,
      userId: userIdFromResponse ? String(userIdFromResponse) : null,
      recordId: payload.recordId || null,
      userShortId: payload.userShortId || null,
      primaryCrmObject: payload.primaryCrmObject || null,
      crmId: payload.crmId || null,
      pdfOnly: payload.pdfOnly || false,
      allowAdditionalSignerDocUpload: payload.allowAdditionalSignerDocUpload || false,
      currentSignerCompleted: Boolean(payload.currentSignerCompleted),
      progress,
      legalContext: payload.legalContext || null,
      contactConsent: hydratedContactConsent,
      termsConsent: false,
    });


  },

  setStructure: (structure) => set({ structure }),

  updateStructure: (newStructure) => set({ structure: newStructure }),

  handleFieldUpdate: (fieldId: string, updates: Partial<FormField>) => {
    const { structure } = get();
    if (!structure?.pages) return;

    const updatedPages = structure.pages.map((page) => ({
      ...page,
      sections: page.sections.map((section) => ({
        ...section,
        fields: section.fields.map((field) =>
          field.id === fieldId ? { ...field, ...updates } : field
        ),
      })),
    }));

    set({ structure: { ...structure, pages: updatedPages } });
  },

  /**
   * Update a field's value
   */
  handleFieldValueChange: (fieldId, value) => {
    set((state) => {
      if (!state.structure) {
        return {};
      }

      let fieldTypeForClearing = '';
      const newPages = state.structure.pages.map((page) => ({
        ...page,
        sections: page.sections.map((section) => ({
          ...section,
          fields: section.fields.map((field) => {
            if (field.id === fieldId) {
              fieldTypeForClearing = field.type;

              

              const updatedField = { ...field, value };


              return updatedField;
            }
            return field;
          }),
        })),
      }));

      const newStructure = { ...state.structure, pages: newPages };

      // Handle file updates separately
      let files = state.files;
      if (fieldTypeForClearing === 'file') {
        files = _clearFilesForFieldId(state.files, fieldId);
        if (Array.isArray(value)) {
          const newFiles: Record<string, File> = {};
          (value as { name: string; contentType: string; file?: File }[]).forEach(
            (fileItem, index) => {
              if (fileItem.file instanceof File) {
                const fileKey = `${fieldId}_${index}`;
                newFiles[fileKey] = fileItem.file;
              }
            }
          );
          files = { ...files, ...newFiles };
        } else if (value instanceof File) {
          files = { ...files, [fieldId]: value };
        }
      }

      return { structure: newStructure, files };
    });
  },

  updateFile: (fieldId: string, file: File) => {
    const files = { ...get().files };
    files[fieldId] = file;
    set({ files });

  },

  setIsEditable: (isEditable) => {
    set({ isEditable });
  },

  // Validation functions removed - now handled by validationStore

  /**
   * Submit the form data to the server
   */
  submitForm: async (
    captchaToken: string,
    mappingShortId: string,
    progressHeaders?: { pageCompletedIndex?: number; pageCompletedId?: string; totalVisiblePages?: number }
  ) => {
    const {
      structure,
      crmId,
      files,
      userShortId,
      primaryCrmObject,
      pdfOnly,
      recordOwnerId,
      contactConsent,
      termsConsent,
    } = get();
    const signerStoreState = useSignerStore.getState();
    const signerIndex = signerStoreState.currentSigner;
    

    // For PDF-only submissions, crmId is optional and signerIndex not required
    const requiredFieldsMissing = !structure || !userShortId || (!pdfOnly && (!crmId || !signerIndex));
    
    if (requiredFieldsMissing) {
      const errorMessage = 'Required form data is not available.';
      logger.error('Application Store', 'submitForm: Pre-submission check failed', {
        hasStructure: !!structure,
        hasCrmId: !!crmId,
        hasUserShortId: !!userShortId,
        signerIndex,
        hasPrimaryCrmObject: !!primaryCrmObject,
        pdfOnly,
        contactConsent,
        termsConsent,
      });
      toast.error(errorMessage);
      return { success: false };
    }

    // Debounce submission to prevent accidental double-clicks
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_DEBOUNCE_MS) {
      logger.warn('Application Store', 'submitForm: Submission attempt blocked by debounce');
      toast.error('Please wait a moment before submitting again.');
      return { success: false };
    }
    lastSubmissionTime = now;

    set({ loading: true, error: null });

    const formData = new FormData();
    formData.append('structure', JSON.stringify(structure));
    formData.append('captchaToken', captchaToken);
    formData.append('mappingShortId', mappingShortId);
    formData.append('userShortId', userShortId);
    if (primaryCrmObject) {
      formData.append('primaryCrmObject', primaryCrmObject);
    }
    if (pdfOnly) {
      formData.append('pdfOnly', 'true');
    }
    formData.append('contactConsent', String(contactConsent));
    formData.append('termsConsent', String(termsConsent));

    // Append files to the FormData - these will be handled separately by the backend
    const fileList: File[] = Object.values(files);
    const fileIds: string[] = Object.keys(files);

    if (fileList.length > 0) {
      fileList.forEach((file) => {
        formData.append('files', file);
      });
      formData.append('fileIds', JSON.stringify(fileIds));
    }

    try {
      const correlationId = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
        ? crypto.randomUUID()
        : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}-${Math.random()
            .toString(16)
            .slice(2, 10)}`;
      const query = new URLSearchParams();
      if (crmId) query.set('crmId', String(crmId));
      if (!pdfOnly && typeof signerIndex === 'number') query.set('signerIndex', String(signerIndex));
      if (pdfOnly) query.set('pdfOnly', '1');
      // Include recordOwnerId for Lead record creation (only used when creating new records)
      if (recordOwnerId) query.set('recordOwnerId', String(recordOwnerId));
      const submissionUrl = `/api/record-actions/submit/${userShortId}/${mappingShortId}${
        query.toString() ? `?${query.toString()}` : ''
      }`;
      const headers: Record<string, string> = {
        'Content-Type': 'multipart/form-data',
        'x-recaptcha-token': captchaToken,
        'x-correlation-id': correlationId,
      };
      if (progressHeaders) {
        const { pageCompletedIndex, pageCompletedId, totalVisiblePages } = progressHeaders;
        if (typeof pageCompletedIndex === 'number') headers['x-page-completed-index'] = String(pageCompletedIndex);
        if (typeof pageCompletedId === 'string') headers['x-page-completed-id'] = pageCompletedId;
        if (typeof totalVisiblePages === 'number') headers['x-total-visible-pages'] = String(totalVisiblePages);
      }
      const { data: _data } = await api.post(
        submissionUrl,
        formData,
        {
          headers,
        }
      );

      set({
        loading: false,
      });

      return {
        success: true,
      };
    } catch (error) {
      set({ loading: false });
      const { recordId, userShortId } = get();
      const { signerShortId } = signerStoreState;
      logger.error('Application Store', 'Form submission failed', {
        recordId,
        userShortId,
        signerShortId,
        error,
        axiosError: error instanceof AxiosError ? error.response?.data : 'N/A',
      });

      // Show a generic error toast to the user
      toast.error(
        'An unexpected error occurred during submission. If the problem persists, please contact support.'
      );

      // Log the specific error message for debugging, but don't set it in the store to avoid UI hanging
      if (error instanceof AxiosError && error.response?.data) {
        const errResponse = error.response.data as ErrorResponse;
        const specificMessage = errResponse.message || 'No specific error message provided.';
        logger.error('Application Store', 'Detailed submission error', { specificMessage });
      }

      return { success: false };
    }
  },

  // State Management
  setLoading: (loading) => {
    set({ loading });
  },

  setError: (error) => {
    // Only log actual errors, not null (error clearing)
    if (error) {
      logger.error('SubmissionFlow', 'Error set', { error });
    }
    set({ error });
  },

  setBrokerTrialEnded: (ended) => {
    set({ isBrokerTrialEnded: ended });
  },

  // Navigation functions moved to navigationStore

  setContactConsent: (consent) => {
    set({ contactConsent: consent });
  },

  setTermsConsent: (consent) => {
    set({ termsConsent: consent });
  },

  setPrimaryCrmObject: (primaryCrmObject) => {
    set({ primaryCrmObject });
  },

  setCrmId: (crmId) => {
    set({ crmId });
  },

  setRecordOwnerId: (recordOwnerId) => {
    set({ recordOwnerId });
  },

  getUserId: () => {
    const { userId } = get();
    return userId || 'unknown';
  },
}));
